import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  getUserLicenses,
  getLicenseActivations,
  resetLicense,
  type License,
  type LicenseActivation
} from "@/lib/license-service";
import {
  Key,
  Download,
  Copy,
  RotateCcw,
  Monitor,
  Calendar,
  AlertCircle,
  CheckCircle,
  Loader2
} from "lucide-react";

interface LicenseManagementProps {
  userId: string;
}

const LicenseManagement = ({ userId }: LicenseManagementProps) => {
  const { toast } = useToast();
  const { t } = useLanguage();

  const [licenses, setLicenses] = useState<License[]>([]);
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [activations, setActivations] = useState<LicenseActivation[]>([]);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);

  useEffect(() => {
    loadLicenses();
  }, [userId]);

  const loadLicenses = async () => {
    try {
      setLoading(true);
      const data = await getUserLicenses(userId);
      setLicenses(data);
    } catch (error) {
      console.error('Error loading licenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadActivations = async (licenseId: string) => {
    try {
      const data = await getLicenseActivations(licenseId);
      setActivations(data);
    } catch (error) {
      console.error('Error loading activations:', error);
    }
  };

  const handleViewActivations = async (license: License) => {
    setSelectedLicense(license);
    await loadActivations(license.id);
  };

  const handleResetLicense = async () => {
    if (!selectedLicense) return;

    try {
      setResetting(true);
      const result = await resetLicense(selectedLicense.license_key);

      if (result.success) {
        toast({
          title: t("รีเซ็ตสำเร็จ!", "Reset Successful!"),
          description: result.message
        });
        setShowResetDialog(false);
        setSelectedLicense(null);
        loadLicenses();
      } else {
        toast({
          title: t("เกิดข้อผิดพลาด", "Error"),
          description: result.error,
          variant: "destructive"
        });
      }
    } catch (error: any) {
      toast({
        title: t("เกิดข้อผิดพลาด", "Error"),
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setResetting(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: t("คัดลอกแล้ว!", "Copied!"),
      description: `${label} ${t("ถูกคัดลอกไปยัง Clipboard", "copied to clipboard")}`
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card variant="glass">
        <CardContent className="p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
        </CardContent>
      </Card>
    );
  }

  if (licenses.length === 0) {
    return (
      <Card variant="glass">
        <CardContent className="p-8 text-center">
          <Key className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold mb-2">{t("ยังไม่มีโปรแกรม", "No Products Yet")}</h3>
          <p className="text-sm text-muted-foreground">
            {t("คุณยังไม่มีโปรแกรมที่ซื้อ", "You haven't purchased any products yet")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {licenses.map((license) => {
        const product = license.products as any;
        const activationPercentage = (license.current_activations / license.max_activations) * 100;

        return (
          <Card key={license.id} variant="glass">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <CardTitle className="text-xl">{product?.name || 'Unknown Product'}</CardTitle>
                    <Badge
                      variant="outline"
                      className={
                        license.status === 'active'
                          ? 'bg-green-500/10 text-green-600 border-green-500/30'
                          : 'bg-red-500/10 text-red-600 border-red-500/30'
                      }
                    >
                      {license.status === 'active' ? t("เปิดใช้งาน", "Active") : t("ระงับ", "Suspended")}
                    </Badge>
                  </div>
                  <CardDescription>
                    Version {product?.version || 'N/A'} • {t("ซื้อเมื่อ", "Purchased")} {formatDate(license.created_at)}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* License Key */}
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  License Key
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-muted rounded-lg p-3">
                    <code className="text-sm font-mono font-bold text-primary">
                      {license.license_key}
                    </code>
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(license.license_key, "License Key")}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Activation Status */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    {t("การเปิดใช้งาน", "Activations")}
                  </label>
                  <span className="text-sm font-semibold">
                    {license.current_activations} / {license.max_activations} {t("เครื่อง", "machines")}
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      activationPercentage >= 100
                        ? 'bg-red-500'
                        : activationPercentage >= 50
                        ? 'bg-yellow-500'
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(activationPercentage, 100)}%` }}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(product?.download_url, '_blank')}
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t("ดาวน์โหลด", "Download")}
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewActivations(license)}
                >
                  <Monitor className="h-4 w-4 mr-2" />
                  {t("ดูเครื่องที่ใช้งาน", "View Machines")} ({license.current_activations})
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedLicense(license);
                    setShowResetDialog(true);
                  }}
                  disabled={license.current_activations === 0}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  {t("รีเซ็ต License", "Reset License")}
                </Button>
              </div>

              {/* Warning if max activations reached */}
              {license.current_activations >= license.max_activations && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 shrink-0" />
                    <p className="text-sm text-yellow-600 dark:text-yellow-400">
                      {t(
                        "ถึงขีดจำกัดการใช้งานแล้ว หากต้องการเปลี่ยนเครื่อง กรุณารีเซ็ต License ก่อน",
                        "Maximum activations reached. Please reset license to change machines"
                      )}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Activations Dialog */}
      {selectedLicense && !showResetDialog && (
        <Dialog open={!!selectedLicense} onOpenChange={() => setSelectedLicense(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t("เครื่องที่เปิดใช้งาน", "Activated Machines")}</DialogTitle>
              <DialogDescription>
                {(selectedLicense.products as any)?.name || 'Unknown Product'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {activations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Monitor className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>{t("ยังไม่มีเครื่องที่เปิดใช้งาน", "No machines activated yet")}</p>
                </div>
              ) : (
                activations.map((activation, index) => (
                  <Card key={activation.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                            <Monitor className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">
                              {activation.machine_name || `Machine ${index + 1}`}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              ID: <code className="text-xs">{activation.machine_id}</code>
                            </p>
                            {activation.os_info && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {activation.os_info}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right text-sm text-muted-foreground">
                          <div className="flex items-center gap-1 mb-1">
                            <Calendar className="h-3 w-3" />
                            <span>{formatDate(activation.activated_at)}</span>
                          </div>
                          {activation.is_active && (
                            <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30 text-xs">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              {t("ใช้งานอยู่", "Active")}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Reset Confirmation Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("ยืนยันการรีเซ็ต License", "Confirm License Reset")}</DialogTitle>
            <DialogDescription>
              {t(
                "การรีเซ็ตจะยกเลิกการเปิดใช้งานบนเครื่องทั้งหมด คุณสามารถเปิดใช้งานใหม่ได้ภายหลัง",
                "This will deactivate all machines. You can activate them again later."
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 shrink-0 mt-0.5" />
              <div className="space-y-2 text-sm">
                <p className="font-medium text-yellow-600 dark:text-yellow-400">
                  {t("คำเตือน", "Warning")}
                </p>
                <p className="text-muted-foreground">
                  {t(
                    "เครื่องที่กำลังใช้งานโปรแกรมจะไม่สามารถใช้งานได้จนกว่าจะเปิดใช้งาน License ใหม่",
                    "Machines currently using the program will stop working until license is reactivated"
                  )}
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowResetDialog(false)}
              disabled={resetting}
            >
              {t("ยกเลิก", "Cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleResetLicense}
              disabled={resetting}
            >
              {resetting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("กำลังรีเซ็ต...", "Resetting...")}
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  {t("รีเซ็ต License", "Reset License")}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LicenseManagement;
